import Component from "../../lib/component";
import createSuggestionPanelStateMachine from "./instances/suggestion_panel_state_machine";
import { uploadFormData } from "../../lib/uploader/upload_request";
import { observeSessionState } from "../../lib/session_state";
import { AuthClient, AuthStatus } from "../../lib/auth/auth_client";
import { resolveMessage, PASSWORD_PANEL_OVERRIDES } from "../../lib/auth/auth_messages";
import { getThrottledFunction } from "../../helpers/throttle";

import { ContributionPanel } from "./components/contribution_panel";
import { IdentityPanel } from "./components/identity_panel";
import { VerifyPanel } from "./components/verify_panel";
import { PasswordPanel } from "./components/password_panel";
import { ForgotPanel } from "./components/forgot_panel";
import { SentPanel } from "./components/sent_panel";

// SuggestionPanel — the in-pane suggestion form on the experience profile
// (plans/2026-07-14--experience-suggestions-with-photos.md).
//
// A staged form on a stage of six look-alike panels (contribution → identity → the auth
// panels → sent), the fourth host built on the shared lib/stage_panel base after the
// MemberOfferAcceptor, the Rankometer and the AccountPanel. The host is a slim orchestrator:
// it owns the state machine, the AuthClient calls, the multipart submit and the session
// prefill. It sets the PHASE and fans render() out; each panel decides for ITSELF whether it
// is the one showing (--active / --inactive on its shared .suggestion_panel__panel block).
//
// Edit and Add Photos are TWO ENTRY POINTS INTO ONE contribution panel — the focus changes
// the title, the hint and the field order; the model, the validation and the endpoint are
// identical. The submit goes to an ABSOLUTE https endpoint with credentials (a relative URL
// on a campingnz:// page is intercepted cookieless), and it fires LAST, once identity is
// settled: a signed-in submitter goes straight through; a signed-out one is offered OTP /
// password auth (with a Skip that submits anonymously). Photos are transcoded to JPEG on the
// contribution panel before they are held, so an abandoned auth leaves no orphan upload.
export const SuggestionPanelConfig = {
  componentTypeKey: "SuggestionPanel",
  baseHtmlClass: "suggestion_panel",
  selector: ".suggestion_panel",

  datasetConfigKey: "suggestion_panel__config",
  endpointKey: "submit_endpoint",

  // The signed-in person's email/name are NEVER server-rendered into this config: the native
  // profile is page cached per platform+version, so a prefilled value would be baked into a
  // file served to everyone else. They are fetched client-side from the shared session-state
  // detector instead (the same cache-split rule the other auth hosts follow).
  sessionStateEndpointKey: "session_state_endpoint",

  // Absolute-https door/ endpoints for the inline auth, delivered via the same dataset config.
  requestOtpEndpointKey: "request_otp_endpoint",
  verifyOtpEndpointKey: "verify_otp_endpoint",
  verifyPasswordEndpointKey: "verify_password_endpoint",
  requestPasswordResetEndpointKey: "request_password_reset_endpoint",
  logoutEndpointKey: "logout_endpoint",

  // The Edit / Add Photos buttons live in the action row, OUTSIDE this element — they are
  // list items in the profile's button list, not children of the panel. So the panel binds
  // them by attribute at document level; every listener is registered with onDestroy, because
  // they outlive the element (the pane re-injects its markup on every open).
  triggerSelector: "[data-suggestion_panel__trigger]",
  triggerAttribute: "data-suggestion_panel__trigger",
  closeSelector: ".suggestion_panel__close",

  stageSelector: ".suggestion_panel__stage",
  stageHeightProperty: "--suggestion-stage-height",

  focusEdit: "EDIT",
  focusPhoto: "PHOTO",

  resizeThrottleMs: 100,
};

export class SuggestionPanel extends Component {
  static get config() {
    return SuggestionPanelConfig;
  }

  constructor(id_or_element) {
    super({
      element: id_or_element,
      config: SuggestionPanelConfig,
      stateMachine: createSuggestionPanelStateMachine(),
    });

    this.setDatasetConfig(SuggestionPanelConfig.datasetConfigKey);
    this.endpoint = this.getDatasetConfigValue(SuggestionPanelConfig.endpointKey);
    this.stageElement = this.element.querySelector(SuggestionPanelConfig.stageSelector);

    // The headless auth engine — server flow + resend clock + cross-surface announce. The
    // host owns the phase logic and the panels; this drives the wire. Absolute-https URLs.
    this.authClient = new AuthClient({
      requestOtpUrl: this.getDatasetConfigValue(SuggestionPanelConfig.requestOtpEndpointKey),
      verifyOtpUrl: this.getDatasetConfigValue(SuggestionPanelConfig.verifyOtpEndpointKey),
      verifyPasswordUrl: this.getDatasetConfigValue(SuggestionPanelConfig.verifyPasswordEndpointKey),
      requestPasswordResetUrl: this.getDatasetConfigValue(SuggestionPanelConfig.requestPasswordResetEndpointKey),
      logoutUrl: this.getDatasetConfigValue(SuggestionPanelConfig.logoutEndpointKey),
    });

    // The six stage panels — siblings, not a hierarchy: the host constructs them and fans
    // render() out; each shares this host's machine and owns its own elements, listeners,
    // fields, visibility and render. Adopted, so destroying this host destroys the panels
    // with it (the pane re-injects markup — the leak the destroy lifecycle exists for).
    this.panels = this.adoptChildComponents(
      this.contributionPanel = new ContributionPanel(this.element.querySelector(ContributionPanel.config.selector), this),
      this.identityPanel = new IdentityPanel(this.element.querySelector(IdentityPanel.config.selector), this),
      this.verifyPanel = new VerifyPanel(this.element.querySelector(VerifyPanel.config.selector), this),
      this.passwordPanel = new PasswordPanel(this.element.querySelector(PasswordPanel.config.selector), this),
      this.forgotPanel = new ForgotPanel(this.element.querySelector(ForgotPanel.config.selector), this),
      this.sentPanel = new SentPanel(this.element.querySelector(SentPanel.config.selector), this),
    );

    const sessionStateEndpoint = this.getDatasetConfigValue(SuggestionPanelConfig.sessionStateEndpointKey);
    if (sessionStateEndpoint) {
      const session = observeSessionState(this.handleSessionState.bind(this), {
        endpointUrl: sessionStateEndpoint,
        onEveryCheck: true,
      });
      this.detector = session.detector; // for a "Not you?" logout re-check (native fan-out)
      this.onDestroy(session.stop); // the detector outlives this panel — detach when it goes
    }

    this.bindTriggers();
    this.bindCloseButtons();

    this.render();

    // The stage height is measured (renderStageHeight), so a viewport change must re-measure.
    // Window listeners outlive the element — hence onDestroy.
    const handleResize = getThrottledFunction(this.renderStageHeight.bind(this), SuggestionPanelConfig.resizeThrottleMs);
    window.addEventListener("resize", handleResize);
    this.onDestroy(() => window.removeEventListener("resize", handleResize));
  }

  // --- Triggers + close ------------------------------------------------------

  bindTriggers() {
    const triggers = document.querySelectorAll(SuggestionPanelConfig.triggerSelector);

    triggers.forEach((trigger) => {
      const handler = (event) => {
        // The Edit / Add Photos triggers keep their href to the standalone suggestion page,
        // so a browser without this bundle still has somewhere to go. When we ARE here, the
        // panel wins.
        if (event) event.preventDefault();
        const focus = trigger.getAttribute(SuggestionPanelConfig.triggerAttribute) || SuggestionPanelConfig.focusEdit;
        this.dispatchAction("openWithFocus", focus);
        // The action row can be most of a phone screen from the panel it just revealed.
        if (this.element.scrollIntoView) this.element.scrollIntoView({ behavior: "smooth", block: "nearest" });
      };

      trigger.addEventListener("click", handler);
      this.onDestroy(() => trigger.removeEventListener("click", handler));
    });
  }

  bindCloseButtons() {
    this.element.querySelectorAll(SuggestionPanelConfig.closeSelector).forEach((button) => {
      button.addEventListener("click", (event) => {
        if (event) event.preventDefault();
        this.dispatchAction("close");
      });
    });
  }

  // --- Session ---------------------------------------------------------------

  // Populate the signed-in identity row + prefill the email, on the person's own device,
  // client-side from the detector. Never clobber what they are actively typing.
  handleSessionState(state) {
    if (this.isDestroyed) return;
    const signedIn = !!(state && state.personId);
    this.dispatchAction("setPerson", signedIn ? { id: state.personId, email: state.email, name: state.displayName } : null);
    if (state && state.email) this.identityPanel.prefillEmail(state.email);
  }

  // --- Step intents ----------------------------------------------------------

  // Continue from the contribution panel. Validate "a message or a photo" here (native
  // validation can't express it) before advancing to identity.
  openIdentity() {
    const message = (this.contributionPanel.messageInput && this.contributionPanel.messageInput.value || "").trim();
    if (!message && !this.files().length) {
      this.dispatchAction("setStatus", { tone: "error", text: "Add a message or a photo." });
      return;
    }
    this.dispatchAction("setStatus", null);
    this.dispatchAction("setPhase", "identity");
  }

  backToContribution() {
    this.dispatchAction("setPhase", "contribution");
  }

  // Continue from the identity panel. Signed in → submit straight away (identity from the
  // cookie). Signed out → email a code (enumeration-uniform: we always attempt verification,
  // never branch on whether the address is registered).
  saveSuggestion() {
    if (this.readStateMachineValue("person")) {
      this.submitSuggestion();
      return;
    }
    const email = this.identityPanel.getEmail().trim();
    if (!email) {
      this.dispatchAction("setAuthStatus", { code: AuthStatus.INVALID_EMAIL, tone: "error", text: "Please add your email address so we can reply." });
      return;
    }
    this.beginVerification(email);
  }

  // --- Auth intents (phase transitions) --------------------------------------

  async beginVerification(email) {
    this.dispatchAction("setAuthEmail", email);
    this.startBusy("SENDING");
    const { statusCode } = await this.authClient.requestCode(email);
    this.endBusy();
    this.applyRequestResult(statusCode);
  }

  applyRequestResult(statusCode) {
    switch (statusCode) {
      case AuthStatus.CODE_SENT:
      case AuthStatus.THROTTLED:
        this.dispatchAction("setPhase", "verify");
        this.dispatchAction("setAuthStatus", resolveMessage(statusCode, {}, this.statusCtx()));
        break;
      case AuthStatus.HAS_USER_ACCOUNT:
        this.dispatchAction("setPhase", "password");
        this.dispatchAction("setAuthStatus", resolveMessage(statusCode, {}, this.statusCtx()));
        break;
      default: // INVALID_EMAIL | NETWORK_ERROR | UNKNOWN_ERROR — stay on identity, show why
        this.dispatchAction("setAuthStatus", resolveMessage(statusCode, {}, this.statusCtx()));
    }
  }

  // Back from the verify / password panels → the identity step, to correct the address.
  changeEmail() {
    this.dispatchAction("resetAuth");
  }

  openForgot() {
    const email = this.authEmail();
    if (email) this.forgotPanel.prefillEmail(email);
    this.dispatchAction("setPhase", "forgot");
    this.dispatchAction("setAuthStatus", null);
  }

  closeForgot() {
    this.dispatchAction("setPhase", "password");
    this.dispatchAction("setAuthStatus", null);
  }

  // Skip auth → submit now via the plain (anonymous / created-with-name) path. No code.
  skipAuth() {
    this.dispatchAction("setAuthStatus", null);
    this.submitSuggestion();
  }

  async resendCode() {
    if (!this.canResend()) return;
    this.startBusy("SENDING");
    const { statusCode } = await this.authClient.requestCode(this.authEmail());
    this.endBusy();
    this.applyRequestResult(statusCode);
  }

  async submitCode(rawCode) {
    const code = (rawCode || "").trim();
    if (!code) {
      this.dispatchAction("setAuthStatus", { code: AuthStatus.INVALID_CODE, tone: "error", text: "Please enter the code from your email." });
      return;
    }
    this.startBusy("CHECKING");
    const { statusCode } = await this.authClient.verifyCode(this.authEmail(), code);
    this.endBusy();
    if (statusCode === AuthStatus.AUTHENTICATED) return this.handleAuthenticated();
    this.dispatchAction("setAuthStatus", resolveMessage(statusCode, {}, this.statusCtx()));
  }

  async submitPassword(rawPassword) {
    const password = rawPassword || "";
    if (!password) {
      this.dispatchAction("setAuthStatus", { code: AuthStatus.INVALID_CREDENTIALS, tone: "error", text: "Please enter your password." });
      return;
    }
    this.startBusy("SIGNING_IN");
    const { statusCode } = await this.authClient.verifyPassword(this.authEmail(), password);
    this.endBusy();
    if (statusCode === AuthStatus.AUTHENTICATED) return this.handleAuthenticated();
    this.dispatchAction("setAuthStatus", resolveMessage(statusCode, PASSWORD_PANEL_OVERRIDES, this.statusCtx()));
  }

  async requestPasswordReset(rawEmail) {
    const email = (rawEmail || "").trim();
    if (!email) {
      this.dispatchAction("setAuthStatus", { code: AuthStatus.INVALID_EMAIL, tone: "error", text: "Please enter your email address." });
      return;
    }
    this.dispatchAction("setAuthEmail", email);
    this.startBusy("SENDING_RESET");
    const { statusCode } = await this.authClient.requestPasswordReset(email);
    this.endBusy();
    this.dispatchAction("setAuthStatus", resolveMessage(statusCode, {}, this.statusCtx()));
  }

  // Successfully authenticated (the client has already announced it) → submit. The name the
  // submitter typed backfills their (nameless, OTP-minted) account server-side.
  handleAuthenticated() {
    this.dispatchAction("setAuthStatus", null);
    this.submitSuggestion();
  }

  // "Not you?" — log the wrong-person session out, clear the identity row + prefilled fields.
  logoutSession() {
    return this.authClient.logout().finally(() => {
      this.dispatchAction("setPerson", null);
      this.dispatchAction("resetAuth");
      this.identityPanel.blankPersonFields();
      if (this.detector) this.detector.check(); // native reconcile + Person-loggedOut fan-out
      this.render();
    });
  }

  // --- Submission ------------------------------------------------------------

  async submitSuggestion() {
    const formData = this.buildFormData();

    this.dispatchAction("setBusy", true);
    this.dispatchAction("setProgress", 0);
    this.dispatchAction("setStatus", { tone: "info", text: "Sending…" });

    try {
      const response = await uploadFormData(this.endpoint, formData, {
        onProgress: (progress) => this.dispatchAction("setProgress", progress),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        if (this.contributionPanel.messageInput) this.contributionPanel.messageInput.value = "";
        this.dispatchAction("setStatus", null);
        this.dispatchAction("sent");
        return;
      }

      this.failSubmission((data && data.message) || "That didn't send. Please try again.");
    } catch (error) {
      // Offline, or the request died mid-flight. Leave the draft and the photos where they
      // are — the person can hit send again once they have signal.
      this.failSubmission("That didn't send — check your connection and try again.");
    }
  }

  // A refused/failed submission lands back on the contribution step with the error, where the
  // draft and photos are still in hand.
  failSubmission(message) {
    this.dispatchAction("setBusy", false);
    this.dispatchAction("setStatus", { tone: "error", text: message });
    this.dispatchAction("setPhase", "contribution");
  }

  // The submission spans two panels (message on contribution, name/email/honeypot on
  // identity); the payload is their fields merged, plus the focus, the page URL and the
  // transcoded files held on the machine.
  buildFormData() {
    const formData = new FormData();
    const message = (this.contributionPanel.messageInput && this.contributionPanel.messageInput.value || "").trim();
    const email = this.identityPanel.getEmail().trim();
    const name = (this.identityPanel.nameInput && this.identityPanel.nameInput.value || "").trim();

    formData.append("suggestion[message]", message);
    formData.append("suggestion[email]", email);
    formData.append("suggestion[name]", name);
    formData.append("suggestion[focus_code]", this.readStateMachineValue("focus"));
    formData.append("suggestion[url]", window.location.href);

    const honeyPot = this.identityPanel.honeyPotInput;
    if (honeyPot) formData.append(`suggestion[${honeyPot.name}]`, honeyPot.value || "");

    this.files().forEach((file) => formData.append("files[]", file));
    return formData;
  }

  // --- Helpers ---------------------------------------------------------------

  files() {
    return this.readStateMachineValue("files") || [];
  }

  authEmail() {
    return this.readStateMachineValue("authEmail");
  }

  statusCtx() {
    return { email: this.authEmail() };
  }

  // The resend cooldown. The AuthClient owns the clock; panels ask the host, never the host's
  // collaborators — one coupling rule, and the panels stay ignorant of the AuthClient.
  canResend() {
    return this.authClient.canResend();
  }

  resendSecondsRemaining() {
    return this.authClient.resendSecondsRemaining();
  }

  startBusy(statusKey) {
    this.dispatchAction("setAuthBusy", true);
    this.dispatchAction("setAuthStatus", resolveMessage(statusKey, {}, this.statusCtx()));
  }

  endBusy() {
    this.dispatchAction("setAuthBusy", false);
  }

  // --- Render ----------------------------------------------------------------

  render() {
    const open = !!this.readStateMachineValue("open");
    const busy = !!this.readStateMachineValue("busy");
    const authBusy = !!this.readStateMachineValue("authBusy");
    const focus = this.readStateMachineValue("focus");

    this.swapHtmlClassStates(open, "open", "closed");
    this.conditionallySetHtmlClassState(busy || authBusy, "busy");
    this.swapHtmlClassStates(focus === SuggestionPanelConfig.focusPhoto, "focus_photo", "focus_edit");

    // Fan out; each panel decides whether it's the one showing. THEN measure the stage.
    this.panels.forEach((panel) => panel.render());
    this.renderStageHeight();
  }

  // The stage's height tracks the ACTIVE panel so phase changes animate the card's size (CSS
  // can't transition height: auto). A zero measurement (hidden panel, jsdom) leaves the
  // pre-JS auto fallback in place.
  renderStageHeight() {
    const phase = this.readStateMachineValue("phase");
    const activePanel = this.panels.find((panel) => panel.config.phase === phase);
    if (!activePanel) return;

    const height = activePanel.element.offsetHeight;
    if (!height) return;

    this.stageElement.style.setProperty(SuggestionPanelConfig.stageHeightProperty, `${height}px`);
  }
}
